import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import type { SessionResponse } from "../shared/api";
import { ClipboardCheck, Database, FileText, GitCompareArrows, History, LayoutDashboard, LogOut, Route, ScrollText, Settings, Users, type LucideIcon } from "lucide-react";
import { ROLE_LABEL } from "../shared/permissions";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import type { VersionInfo } from "@/components/Brand";
import { LoginScreen } from "@/components/LoginScreen";
import { AuditLogScreen } from "@/components/AuditLogScreen";
import { SourcesScreen } from "@/components/SourcesScreen";
import { UploadDetailScreen } from "@/components/UploadDetailScreen";
import { HistoryScreen } from "@/components/HistoryScreen";
import { ReconciliationScreen } from "@/components/ReconciliationScreen";
import { IpaScreen } from "@/components/IpaScreen";
import { VersionFooter } from "@/components/Brand";
import { AppShellSkeleton } from "@/components/Loading";
import { DashboardScreen } from "@/components/DashboardScreen";
import { ReportScreen } from "@/components/ReportScreen";
import { TraceScreen } from "@/components/TraceScreen";
import { SetPasswordScreen } from "@/components/SetPasswordScreen";
import { AccountScreen } from "@/components/AccountScreen";
import { UsersScreen } from "@/components/UsersScreen";
import { SettingsScreen } from "@/components/SettingsScreen";
import { go, navigate } from "@/lib/nav";
import { LocationContext } from "@/lib/location";
import { PRODUCT } from "@/lib/product";
import { getHealth, getSession, logout, setSessionLostHandler } from "@/lib/client";
import { LOGIN, menuFor, redirectFor } from "@/lib/guard";
import { ACCOUNT, defaultPath, screens, uploadIdOf } from "./screens";


/** Menü ikonları (ui.md §12.5). */
const MENU_ICON: Record<string, LucideIcon> = {
  E1: LayoutDashboard, E2: Database, E3: GitCompareArrows, E4: Route, E5: ClipboardCheck, E6: History, E7: FileText, E8: ScrollText, E9: Users, E10: Settings,
};
const ADMIN_IDS = ["E8", "E9", "E10"];
const ITEM = "relative data-[active=true]:before:absolute data-[active=true]:before:inset-y-1.5 data-[active=true]:before:left-0 data-[active=true]:before:w-0.5 data-[active=true]:before:rounded-full data-[active=true]:before:bg-sidebar-primary";

// Client-side routing on the History API; the server answers every path with index.html (ADR-0001).
function subscribe(cb: () => void) {
  window.addEventListener("popstate", cb);
  return () => window.removeEventListener("popstate", cb);
}
const getPath = () => window.location.pathname;
const getServerPath = () => defaultPath;

/** After the password is set: re-read the session; on a network error show the login screen instead of a stuck button (#71). */
export const reloadSession = (set: (s: SessionResponse | null) => void) => getSession().then(set).catch(() => set(null));

/** `session` prop is for tests (skips the fetch). undefined = loading, null = signed out. */
export function App({ path: forcedPath, session: forcedSession, company: forcedCompany, version: forcedVersion }: { path?: string; session?: SessionResponse | null; company?: string; version?: VersionInfo } = {}) {
  const current = useSyncExternalStore(subscribe, getPath, getServerPath);
  const path = forcedPath ?? current;
  const [fetched, setSession] = useState<SessionResponse | null | undefined>(undefined);
  const session = forcedSession !== undefined ? forcedSession : fetched;

  useEffect(() => {
    if (forcedSession !== undefined) return;
    const load = () => getSession().then(setSession, () => setSession(null));
    setSessionLostHandler((code) => (code === "UNAUTHENTICATED" ? setSession(null) : void load()));
    void load();
  }, [forcedSession]);

  const [location, setLocation] = useState<ReactNode>(null); // alt sayfanın üst çubuk konumu (ui.md §12.3)
  const [company, setCompany] = useState<string | null>(forcedCompany ?? null);
  const [version, setVersion] = useState<VersionInfo | null>(forcedVersion ?? null);
  useEffect(() => { // tek istek: firma adı ve sürüm /api/health'ten (ui.md §10.4)
    if (forcedCompany === undefined && forcedVersion === undefined) void getHealth().then((h) => { setCompany(h?.company ?? null); setVersion(h ? { version: h.version, build: h.build } : null); });
  }, [forcedCompany, forcedVersion]);

  const target = redirectFor(path, session);
  useEffect(() => { if (target && forcedPath === undefined) go(target, true); }, [target, forcedPath]);

  const uploadId = uploadIdOf(path);
  const sub = uploadId !== undefined;
  const menuScreen = session ? menuFor(session.role).find((s) => s.path === (sub ? "/sources" : path)) : undefined;
  const onAccount = path === ACCOUNT;
  const title = session === null ? "Giriş" : session?.mustSetPassword ? "Parola belirleme" : onAccount ? "Hesabım" : sub && menuScreen ? (uploadId === null ? "Yükleme bulunamadı" : menuScreen.title) : menuScreen ? menuScreen.title : screens.some((s) => s.path === path) ? "Yetki yok" : "Sayfa bulunamadı";
  useEffect(() => { if (session !== undefined) document.title = `${title} · ${PRODUCT}`; }, [session, title]);

  if (session === undefined || target) return <AppShellSkeleton />;
  const signedOut = () => setSession(null);
  if (session === null) return <LoginScreen company={company} version={version} onLogin={setSession} />;
  if (session.mustSetPassword) {
    return <SetPasswordScreen company={company} version={version} onDone={() => void reloadSession(setSession)} onLogout={() => void logout().finally(signedOut)} />;
  }

  const menu = menuFor(session.role);
  const known = screens.find((s) => s.path === path);
  const screen = onAccount ? undefined : menu.find((s) => s.path === (sub ? "/sources" : path)); // alt sayfada üst ekran menüde aktif kalır

  return (
    <TooltipProvider>
      <SidebarProvider>
        <Sidebar>
          <SidebarHeader className="flex-row items-center gap-3 px-4 py-4">
            <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary font-semibold text-sidebar-primary-foreground">Y</span>
            <div className="min-w-0">
              <p className="text-base font-semibold leading-tight text-sidebar-accent-foreground">{PRODUCT}</p>
              {company && <p title={company} className="truncate text-xs text-sidebar-muted">{company}</p>}
            </div>
          </SidebarHeader>
          <SidebarContent>
            <nav aria-label="Ana menü">
              {[menu.filter((s) => !ADMIN_IDS.includes(s.id)), menu.filter((s) => ADMIN_IDS.includes(s.id))].map((items, g) => items.length > 0 && (
                <SidebarGroup key={g}>
                  {g === 1 && <SidebarGroupLabel className="text-sidebar-muted">Yönetim</SidebarGroupLabel>}
                  <SidebarMenu>
                    {items.map((s) => {
                      const Icon = MENU_ICON[s.id]!;
                      return (
                        <SidebarMenuItem key={s.id}>
                          <SidebarMenuButton asChild isActive={s.id === screen?.id} className={ITEM}>
                            <a
                              href={s.path}
                              data-menu-item={s.id}
                              aria-current={s.id === screen?.id ? (sub ? "true" : "page") : undefined}
                              onClick={(e) => navigate(e, s.path)}
                            >
                              <Icon aria-hidden="true" /><span>{s.title}</span>
                            </a>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroup>
              ))}
            </nav>
          </SidebarContent>
          <SidebarFooter className="flex-row items-center gap-2 border-t border-sidebar-border px-4 py-3">
            <a
              href={ACCOUNT}
              data-account-link
              aria-current={onAccount ? "page" : undefined}
              onClick={(e) => navigate(e, ACCOUNT)}
              className="-mx-2 flex min-w-0 flex-1 items-center gap-3 rounded-md px-2 py-1 text-sm outline-none hover:bg-sidebar-accent focus-visible:ring-[3px] focus-visible:ring-sidebar-ring aria-[current=page]:bg-sidebar-accent"
            >
              <UserAvatar name={session.username} size="md" />
              <span className="min-w-0"><span className="block truncate font-medium text-sidebar-accent-foreground">{session.username}</span><span className="block text-xs text-sidebar-muted">{ROLE_LABEL[session.role]}</span></span>
            </a>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Çıkış" className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:border-sidebar-ring focus-visible:ring-sidebar-ring" onClick={() => void logout().finally(() => { signedOut(); go(LOGIN, true); })}><LogOut aria-hidden="true" /></Button>
              </TooltipTrigger>
              <TooltipContent side="top">Çıkış</TooltipContent>
            </Tooltip>
          </SidebarFooter>
        </Sidebar>
        <SidebarInset className="min-w-0">
          <LocationContext.Provider value={setLocation}>
          <header className="flex h-14 items-center gap-2 border-b bg-card px-4">
            <SidebarTrigger aria-label="Menüyü aç veya kapat" />
            <Separator orientation="vertical" className="mx-1 data-[orientation=vertical]:h-5" />
            <div data-slot="location" className="min-w-0 text-sm font-medium">{(sub && location) || (onAccount ? "Hesabım" : screen?.title)}</div>
          </header>
          {onAccount ? <AccountScreen /> : !screen ? (
            <main className="p-6">
              <h1 className="text-2xl font-semibold">{known ? "Bu ekranı görme yetkiniz yok." : "Sayfa bulunamadı"}</h1>
              {!known && <p className="mt-2 text-muted-foreground">Bu adreste bir ekran yok.</p>}
              <a href={defaultPath} onClick={(e) => navigate(e, defaultPath)} className="mt-4 inline-block text-sm text-primary underline-offset-4 hover:underline">Durum Panosu'na dön</a>
            </main>
          ) : screen.id === "E10" ? <SettingsScreen /> : screen.id === "E9" ? <UsersScreen me={session.username} role={session.role} /> : screen.id === "E8" ? <AuditLogScreen /> : screen.id === "E1" ? <DashboardScreen /> : screen.id === "E2" ? (sub ? <UploadDetailScreen key={path} id={uploadId} role={session.role} /> : <SourcesScreen role={session.role} />) : screen.id === "E6" ? <HistoryScreen /> : screen.id === "E5" ? <IpaScreen role={session.role} /> : screen.id === "E3" ? <ReconciliationScreen role={session.role} /> : screen.id === "E4" ? <TraceScreen /> : screen.id === "E7" ? <ReportScreen /> : (
            <main className="p-6">
              <h1 className="text-2xl font-semibold">{screen.title}</h1>
              <p className="mt-2 text-muted-foreground">{screen.description}</p>
            </main>
          )}
          <div className="mt-auto"><VersionFooter info={version} /></div>
          </LocationContext.Provider>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}
